import { useEffect, useRef, useState } from "react";

/**
 * IntersectionObserver를 사용하여 요소의 가시성을 감지하는 훅
 * 
 * @param threshold - 요소가 얼마나 보여야 가시성으로 판단할지 (0.0 ~ 1.0, 기본값: 0.3)
 * @returns ref - 감지할 요소에 연결할 ref
 * @returns isVisible - 요소가 화면에 보이는지 여부 (한번 true가 되면 계속 true 유지)
 * 
 * @example
 * ```tsx
 * const { ref, isVisible } = useIntersectionVisible({ threshold: 0.5 });
 * 
 * useEffect(() => {
 *   if (!isVisible) return;
 *   // 화면에 보일 때만 실행할 로직
 *   track("section_impression", { ... });
 * }, [isVisible]);
 * 
 * return <div ref={ref}>...</div>
 * ```
 */
export function useIntersectionVisible(options?: {
    threshold?: number;
    rootMargin?: string;
}) {
    const ref = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (!ref.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    // 한번 보이면 계속 true 유지 (재진입 시 중복 추적 방지)
                    if (entry.isIntersecting && !isVisible) {
                        setIsVisible(true);
                    }
                });
            },
            {
                threshold: options?.threshold ?? 0.3,
                rootMargin: options?.rootMargin ?? "0px",
            }
        );

        observer.observe(ref.current);

        return () => {
            observer.disconnect();
        };
    }, [isVisible, options?.threshold, options?.rootMargin]);

    return { ref, isVisible };
}

